import { DebounceInput } from "@/components/DebounceInput";
import { FilterType } from "@/lib/filters";
import { cn } from "@/lib/utils";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDataTableSettings } from "../hooks/useDataTableSettings";
import { useFilterState } from "../hooks/useFilterState";
import FilterTypeDropDown from "./FilterTypeDropDown";
import { NumberInput } from "@/components/ui/NumberInput";

interface NumberFilterProps {
  columnId: string;
}

export const NumberFilter = ({ columnId }: NumberFilterProps) => {
  const { options } = useDataTableSettings();
  const { t } = useTranslation();
  const {
    filterType,
    onFilterTypeChange,
    readOnly,
    isFilteredOrSorted,
    columnFilterValue,
    query,
  } = useFilterState({ columnId });

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.replace(",", ".");
      options.setFilterValue(columnId, {
        value: value,
        filter: filterType,
      });
    },
    [filterType, options, columnId],
  );

  const onInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // We mark the column immediately as filtered if the user has entered a value.
      options.setFilteredMark(columnId, event.target.value !== "");
    },
    [options, columnId],
  );

  const value = useMemo(() => {
    // For "isEmpty" or "isNotEmpty" we don't need the value,
    // therefore we want to return the translated string
    // instead of the empty string or value provided by the user.
    switch (filterType) {
      case FilterType.IsEmpty:
        return t("table.filters.isEmpty");
      case FilterType.IsNotEmpty:
        return t("table.filters.isNotEmpty");
      default:
        return (columnFilterValue?.value ?? "") as string;
    }
  }, [columnFilterValue, filterType, t]);

  const showSpinner = useMemo(() => {
    return query.isFetching && value !== "";
  }, [query.isFetching, value]);

  return (
    <div className="relative">
      <FilterTypeDropDown
        filterType={filterType}
        onChange={onFilterTypeChange}
        className={cn(
          "absolute left-2 top-2 h-6 w-6 bg-white/80",
          readOnly && "hover:bg-white",
        )}
        showSpinner={showSpinner}
      />
      <DebounceInput
        placeholder={t("common.search")}
        className={cn("p-2 pl-9 text-sm leading-3 h-10", {
          "text-slate-400 bg-slate-100 cursor-default": readOnly,
          "border-blue-500/50": isFilteredOrSorted,
        })}
        onChange={onChange}
        value={value}
        debounce={750}
        readOnly={readOnly}
        onOriginalInput={onInput}
        as={NumberInput}
      />
    </div>
  );
};
